
  import { Component, Prop, Vue } from "vue-property-decorator";
  import { NOTIFICATION } from "@/entities/Events";
  import Message, { MessageDuration } from "@/data/notificationsMessages";
  import { defaultErrorMessage } from "@/data/errorMessages";
  import { Project_Activity, Project_Activity_Set_Input } from "@/generated/graphql";
  import UPDATE_PROJECT_ACTIVITY from "@/apollo/projectActivities/UpdateActivity.gql";
  import CardForm from "@/components/card/CardForm.vue";
  import CardLabel from "@/data/cardLabels";
  import addDollarSign from "@/helpers/addDollarSign";
  import PrefixedFormInput from "@/components/form/PrefixedFormInput.vue";

  interface ShowSuccessMessageArgs {
    recordUpdated: boolean,
    successMessage: string,
    failMessage: string,
  }

  interface IsActivityUpdatedArgs {
    update_project_activity_by_pk: Project_Activity | null,
  }

  interface ConfirmUpdateArgs {
    optionName: string,
    controversialOptionValue: number | undefined,
    controversialOptionName: string,
    action: () => Promise<void>
  }

  @Component({
    components: {
      PrefixedFormInput,
      CardForm,
    },
  })
  export default class ContractorReport extends Vue {
    @Prop({ type: String, required: true }) readonly activityId!: string;
    @Prop({ type: Number, default: undefined }) readonly finalHours?: number;
    @Prop({ type: Number, default: undefined }) readonly finalCost?: number;

    finalHoursText: string = CardLabel.FinalHours;
    finalCostText: string = CardLabel.FinalCost;
    finalHoursProcessing: boolean = false;
    finalCostProcessing: boolean = false;
    finalCostValue: number = 0;

    get finalCostContent(): string {
      return addDollarSign(this.finalCost);
    }

    async confirmUpdate({ optionName, controversialOptionValue, controversialOptionName, action }: ConfirmUpdateArgs): Promise<void> {
      if (!controversialOptionValue) {
        return action();
      }

      this.$confirm({
        title: `The '${controversialOptionName}' will be replaced by the '${optionName}'`,
        message: `Are you sure you want to replace?`,
        button: {
          no: "Cancel",
          yes: "Replace",
        },
        callback: async (confirm: any) => {
          if (confirm) {
            return action();
          }
        },
      });
    }

    async updateFinalHours(hours: number): Promise<void> {
      this.finalHoursProcessing = true;

      try {
        const rawData = await this.updateActivity({
          final_hours: hours,
          final_cost: null,
        });

        const recordUpdated = this.isActivityUpdated(rawData.data);
        const successMessage = `Final number of hours updated successfully`;
        const failMessage = `The final number of hours has not been updated since the previous value was confirmed by the company's management`;

        this.showUpdateResultMessage({ recordUpdated, successMessage, failMessage });

        this.$emit("updated");
      } catch (_) {
        this.$eventBus.emit(NOTIFICATION, Message.errorMessage(defaultErrorMessage));
      } finally {
        this.finalHoursProcessing = false;
      }
    }

    async updateFinalCost(cost: number) {
      this.finalCostProcessing = true;

      try {
        const rawData = await this.updateActivity({
          final_cost: cost,
          final_hours: null,
        });

        const recordUpdated = this.isActivityUpdated(rawData.data);
        const successMessage = `Final cost updated successfully`;
        const failMessage = `The final cost has not been updated since the previous value was confirmed by the company's management`;

        this.showUpdateResultMessage({ recordUpdated, successMessage, failMessage });

        this.$emit("updated");
      } catch (_) {
        this.$eventBus.emit(NOTIFICATION, Message.errorMessage(defaultErrorMessage));
      } finally {
        this.finalCostProcessing = false;
      }
    }

    updateActivity(set: Project_Activity_Set_Input) {
      return this.$apollo.mutate({
        mutation: UPDATE_PROJECT_ACTIVITY,
        variables: {
          id: this.activityId,
          set,
        },
      });
    }

    showUpdateResultMessage({ recordUpdated, successMessage, failMessage }: ShowSuccessMessageArgs): void {
      if (recordUpdated) {
        this.$eventBus.emit(
          NOTIFICATION,
          Message.successMessage(successMessage)
        );
        return;
      }

      this.$eventBus.emit(
        NOTIFICATION,
        Message.infoMessage(failMessage, MessageDuration.LONG)
      );
    }

    isActivityUpdated(data: IsActivityUpdatedArgs): boolean {
      return Boolean(data.update_project_activity_by_pk);
    }
  }
